import { array } from '@ember/helper';
import Button from 'tio-ui/components/button';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import SearchInput from 'tio-ui/components/search-input';
import Select from 'tio-ui/components/select';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import PageHeader from 'tio-ui/components/page-header';
import { PhUsers } from 'ember-phosphor-icons';
import type { EmployeeIndexModel } from 'tio-employee/routes/authenticated/admin/employees/index';
import type EmployeesIndexController from 'tio-employee/controllers/authenticated/admin/employees/index';
import type RouterService from '@ember/routing/router';

export interface S {
  Args: {
    controller: EmployeesIndexController;
    model: EmployeeIndexModel;
  };
}

export default class EmployeeIndexRoute extends Component<S> {
  @service declare router: RouterService;

  didSearch = (string: string) => {
    this.args.controller.search = string;
    this.args.controller.page = 1;
  };

  createEmployeeTransition = () => {
    this.router.transitionTo('authenticated.admin.employees.add');
  };

  onSort = (sort: string) => {
    this.args.controller.sort = sort;
  };

  previousPage = () => {
    this.args.controller.page = Math.max(1, this.args.controller.page - 1);
  };

  nextPage = () => {
    ++this.args.controller.page;
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = page;
  };

  <template>
    {{pageTitle (t "partner.employees")}}

    <PageHeader @title={{t "partner.employees"}} @icon={{PhUsers}}>
      <:breadcrumbs as |b|>
        <b.crumb @label="Admin" />
        <b.crumb @label="Employees" />
      </:breadcrumbs>
      <:toolbar>
        <SearchInput
          @label={{t "partner.employee.search"}}
          @placeholder="Search (Name, Payroll ID, Email)"
          @value={{@controller.search}}
          @onChange={{this.didSearch}}
          @onSearch={{this.didSearch}}
        />
        <Select
          @label="Filter by status"
          @clearable={{true}}
          @items={{array "Participating" "Pending" "Deactivated"}}
          @selected={{@controller.participationStatus}}
          @onChange={{fn (mut @controller.participationStatus)}}
        />
      </:toolbar>
      <:actions>
        <Button
          @intent="primary"
          @appearance="outlined"
          {{on "click" this.createEmployeeTransition}}
        >
          {{t "partner.employee.create_employee"}}
        </Button>
      </:actions>
    </PageHeader>

    <VStack>
      <Section class="overflow-x-auto">
        <:body>
          {{! deferred to #8924 follow-up: plan/eligibility/accounts/loans }}
          <Table @isLoading={{@model.isLoading}}>
            <:thead as |options|>
              <options.tr>
                <options.th
                  @sortField="payroll-id"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.payroll_id"}}
                </options.th>
                <options.th
                  @sortField="first-name"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.first_name"}}
                </options.th>
                <options.th
                  @sortField="last-name"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.last_name"}}
                </options.th>
                <options.th
                  @sortField="email"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.email"}}
                </options.th>
                <options.th
                  @sortField="participation-status"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.status"}}
                </options.th>
                <options.th
                  @sortField="created-at"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.activation_date"}}
                </options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each @model.items as |employee|}}
                <options.tr>
                  <options.td>
                    <LinkTo
                      class="text-primary-500 underline"
                      @route="authenticated.admin.employees.show"
                      @model={{employee.id}}
                    >
                      {{employee.payrollId}}
                    </LinkTo>
                  </options.td>
                  <options.td>
                    <LinkTo @route="authenticated.admin.employees.show" @model={{employee.id}}>
                      {{employee.firstName}}
                    </LinkTo>
                  </options.td>
                  <options.td>
                    <LinkTo @route="authenticated.admin.employees.show" @model={{employee.id}}>
                      {{employee.lastName}}
                    </LinkTo>
                  </options.td>
                  <options.td>{{employee.email}}</options.td>
                  <options.td>{{employee.participationStatus}}</options.td>
                  <options.td>
                    {{safeFormatDate employee.createdAt month="long" day="2-digit" year="numeric"}}
                  </options.td>
                </options.tr>
              {{/each}}
            </:tbody>
            <:tfoot>
              <tr>
                <td class="text-right" colspan="6">
                  <Paginator
                    @simple={{true}}
                    @currentPage={{@controller.page}}
                    @totalItems={{@model.total}}
                    @pageSize={{@controller.limit}}
                    @onChange={{this.changePaginatorPage}}
                  />
                </td>
              </tr>
            </:tfoot>
          </Table>
        </:body>
      </Section>
    </VStack>
  </template>
}
